/**
 * Created by Carsten Hibbeler on 07.02.2016.
 */
'use strict';

import React from 'external/react';
import PropTypes from 'external/prop-types';
import {
    amdRequire,
    FormActions,
    PluginComponent,
    UidUtil,
    wrapWithCommonFormContainer
} from 'plugin/liferay-widgets-react-web';

import { Dropdown } from 'external/react-bootstrap';
import '../css/_delayedScroll.scss';
import { JournalEntryAddForm } from '../components/JournalEntryAddForm';
import { DateFormat } from '../base-portlet';

const JournalEntryAddFormHoc = wrapWithCommonFormContainer(JournalEntryAddForm);
const dependencies = [
    'highcharts',
    'highcharts-export',
    'highcharts-offline-export',
    'highcharts-map',
    'highcharts-more',
    'highcharts-boost',
    'highcharts-no-data'
];

const HC_OPTIONS = {
    global: {
        useUTC: true,
        timezoneOffset: new Date().getTimezoneOffset()
    },
    lang: {
        months: [ 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember' ],
        shortMonths: [ 'Jan', 'Feb', 'März', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sept', 'Okt', 'Nov', 'Dez' ],
        weekdays: [ 'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag' ],
        rangeSelectorFrom: 'Von',
        rangeSelectorTo: 'Bis',
        loading: 'Laden...',
        resetZoom: 'Zurücksetzen',
        downloadJPEG: 'JPEG herunterladen',
        downloadPDF: 'PDF herunterladen',
        downloadPNG: 'PNG herunterladen',
        downloadSVG: 'SVG herunterladen',
        printChart: 'Drucken'
    },

};

const _printChart = (chartKey, title) => {
    const prtContent = document.getElementById(chartKey);
    const WinPrint = window.open('', title || '');

    const html = `
            <body>
                ${ prtContent.innerHTML }
                <footer class="p-3">
                    ${ DateFormat.formatDateTimeNoSeconds(Date.now()) } Uhr, ${ window.themeDisplay.getUserName() }
                </footer>
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
        `;
    WinPrint.document.write(html);
    WinPrint.document.close();
    WinPrint.focus();

    WinPrint.onafterprint = function (event) {
        WinPrint.close();
    };
    WinPrint.print();
};

const exportPDF = (chart, title) => {
    chart && chart.exportChart({
            filename: title || 'chart',
            type: 'application/pdf',
            scale: 1
        },
        {
            title: { text: title || '' },
            chart: {
                height: 600,
                width: 1200,
                spacing: [ 10, 25, 15, 10 ]
            }
        });
};

const getExportData = (chart, hc, title) => {
    return new Promise((resolve, reject) => {
        if (chart) {
            //const { chartWidth, chartHeight } = chart;
            const chartWidth = 700;
            const chartHeight = 400;

            let chartOptions = {
                title: false,
                chart: {
                    height: chartHeight,
                    width: chartWidth,
                    spacing: [ 10, 25, 15, 10 ]
                }
            };
            let exportingOptions = {
                filename: title || 'chart',
                type: 'image/svg+xml',
                sourceWidth: chartWidth,
                sourceHeight: chartHeight,
            };
            exportingOptions = hc.merge(chart.options.exporting, exportingOptions);
            const svg = chart.getSVGForExport(exportingOptions, chartOptions);

            const canvas = document.createElement('canvas');
            canvas.width = chartWidth;
            canvas.height = chartHeight;
            const ctx = canvas.getContext('2d');
            const image = new Image(chartWidth, chartHeight);
            image.onload = () => {
                const mimetype = 'image/jpeg';
                ctx.drawImage(image, 0, 0);
                canvas.toBlob((blob) => {
                    resolve({
                        filename: exportingOptions.filename || 'chart',
                        type: mimetype,
                        width: chartWidth,
                        height: chartHeight,
                        blob
                    });

                }, mimetype, 0.95);
            };

            image.onerror = (e) => {
                console.error('ERROR converting chart to blob', e);
                reject(e);
            };

            image.src = `data:image/svg+xml,${ encodeURIComponent(svg) }`;
        } else {
            reject('No chart');
        }
    });
};

const exportImage = (chart, title, type) => {
    chart && chart.exportChartLocal({
            filename: title || 'chart',
            type: 'image/' + type,
            scale: 1
        },
        {
            title: { text: title || '' },
            chart: {
                height: 600,
                width: 1200,
                spacing: [ 10, 25, 15, 10 ]
            }
        });
};

class HighChart extends PluginComponent {

    constructor(props) {
        super(props, props.plugins, dependencies);
        this.state = {
            ...this.state,
            chartKey: props.chartKey || UidUtil.generateId('chart'),
            chartLoaded: false
        };

        this.DATA = {
            FORM_ADD_JOURNAL_ENTRY: UidUtil.generateId('FormAddJournalEntry')
        };

        this.scrollOverlay = null;
        this.focusDiv = this.focusDiv.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.enable = this.enable.bind(this);
        this.disable = this.disable.bind(this);
        this.onSelect = this.onSelect.bind(this);
        this.onJournalEntryAddFormSubmit = this.onJournalEntryAddFormSubmit.bind(this);
    }

    componentDidMount() {
        super.componentDidMount();
        this.disable();
    }

    componentWillUnmount() {
        if (this.chart) {
            this.chart.destroy();
        } else {
            console.error('Chart componentWillUnmount without existing chart!');
        }
        super.componentWillUnmount();
    }

    initWithPlugins(o) {
        console.log('Init chart! options: ', o, ' plugins: ', this.plugins());
        const { dependencies: d } = this.state;
        const { highcharts: hc } = d;
        hc.seriesTypes.area.prototype.drawLegendSymbol = hc.seriesTypes.line.prototype.drawLegendSymbol;
        d[ 'highcharts-export' ](hc);
        d[ 'highcharts-offline-export' ](hc);
        d[ 'highcharts-map' ](hc);
        d[ 'highcharts-more' ](hc);
        d[ 'highcharts-boost' ](hc);
        d[ 'highcharts-no-data' ](hc);

        hc.setOptions(HC_OPTIONS);

        hc.PlotLineOrBand.prototype.update = function (newOptions) {
            let plotBand = this;
            hc.extend(plotBand.options, newOptions);
            if (plotBand.svgElem) {
                plotBand.svgElem.destroy();
                plotBand.svgElem = undefined;
                plotBand.render();
            }
        };
        this.hc = hc;
        this.chart = hc.chart(this.refChart, {
            exporting: {
                enabled: false
            },
            ...o,
            chart: {
                ...o.chart,
                events: {
                    ...o.chart.events,
                    load: () => {
                        this.setState({
                            chartLoaded: true
                        });
                    }
                }
            }
        });

        Array.prototype.forEach.call(document.getElementsByClassName('modal-body'), (elem) => {
            if (elem.contains(this.scrollOverlay)) {
                this.modal = elem;
                this.modalScrollable = elem.scrollHeight > elem.clientHeight;
                if (!this.modalScrollable) {
                    this.scrollOverlay && this.scrollOverlay.classList.remove('enter');
                }
            }
        });

        this.updateChart(this.chart, o);
        this.callPluginsInit(this.chart, o);
    }

    reinitWithPlugins(o) {
        console.log('Reinit chart! options: ', o, ' plugins: ', this.plugins());
        if (this.chart) {
            this.chart.destroy();
        }
        this.initWithPlugins(o);
    }

    updateWithPlugins(o) {
        this.updateChart(this.chart, o);
        this.callPluginsUpdate(this.chart, o);
    }

    updateChart(chart, o) {
        const { height } = o;
        if (height && (height !== chart.container.currentHeight)) {
            chart.setSize(null, height);
        }
    }

    enable(e) {
        if (e.ctrlKey) {
            this.scrollOverlay && this.scrollOverlay.classList.remove('enter');
        }
    }

    disable(e) {
        if (!this.modal || this.modalScrollable) {
            this.scrollOverlay && this.scrollOverlay.classList.add('enter');
        }
    }

    onTouchStart() {
        this.scrollOverlay && this.scrollOverlay.classList.remove('enter');
    }

    focusDiv(e) {
        if (this.scrollOverlay) {
            this.scrollOverlay.focus();
        }
    }

    onSelect(eventKey, e) {
        const { title, printChart } = this.props;
        const { chartKey } = this.state;
        if (eventKey === 'print') {
            printChart ? printChart(chartKey, title) : _printChart(chartKey, title);
        } else if (eventKey === 'pdf') {
            exportPDF(this.chart, title);
        } else if (eventKey === 'journalEntry') {
            FormActions.startEditFormItem(this.DATA.FORM_ADD_JOURNAL_ENTRY, { title });
        } else {
            exportImage(this.chart, title, eventKey);
        }
    }

    onJournalEntryAddFormSubmit(formData) {
        const { getNewJournalEntryData } = this.props;
        getExportData(this.chart, this.hc, formData.title)
            .then((exportData) => {
                const entryData = (getNewJournalEntryData && getNewJournalEntryData()) || {};
                if (exportData.blob) {
                    entryData.attachments = { chart: exportData.blob };
                    delete exportData.blob;
                }
                entryData.message = {
                    chart: exportData,
                    ...formData,
                    ...entryData.message,
                };
                entryData.groupId = Liferay.ThemeDisplay.getScopeGroupId();
                console.info(entryData);
                amdRequire([ 'journal-portlet/js/journal-portlet' ], (J) => {
                    J.JournalActions.addEntry(entryData, this.DATA.FORM_ADD_JOURNAL_ENTRY, true);
                });
            })
            .catch((e) => {
                //todo
                console.error(e);
            });
    }

    render() {
        const { editJournalTitle } = this.props;
        const { chartLoaded } = this.state;

        return (
            <div>
                {
                    chartLoaded &&
                    <Dropdown
                        variant="default"
                        className="highcharts-export-button"
                        onSelect={ this.onSelect }
                    >
                        <Dropdown.Toggle variant="outline-primary" size="lg">
                            <i className="fa fa-bars"/>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item eventKey="print">Drucken</Dropdown.Item>
                            <Dropdown.Item eventKey="journalEntry">Journaleintrag erstellen</Dropdown.Item>
                            <Dropdown.Divider/>
                            <Dropdown.Item eventKey="png">PNG herunterladen</Dropdown.Item>
                            <Dropdown.Item eventKey="jpeg">JPG herunterladen</Dropdown.Item>
                            <Dropdown.Item eventKey="pdf">PDF herunterladen</Dropdown.Item>
                            <Dropdown.Item eventKey="svg+xml">SVG herunterladen</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                }
                <div
                    className="delayed-chart-scroll-overlay"
                    ref={ div => this.scrollOverlay = div }
                    tabIndex="0"
                    onMouseEnter={ this.focusDiv }
                    onTouchStart={ this.onTouchStart }
                    onKeyDown={ this.enable }
                    onKeyUp={ this.disable }
                    onBlur={ this.disable }
                >
                    <JournalEntryAddFormHoc
                        formId={ this.DATA.FORM_ADD_JOURNAL_ENTRY }
                        showReset={ true }
                        onFormSubmit={ this.onJournalEntryAddFormSubmit }
                        modalProps={ {
                            aboveLiferayDialog: true,
                            size: 'md'
                        } }
                        editTitle={ editJournalTitle }
                    />

                    <div ref={ c => this.refChart = c } id={ this.state.chartKey } style={ this.props.style }
                         className={ this.props.className }>
                        <span>Chart wird geladen...</span><br/>
                        { this.state.loadPlugins && <span>Plugins werden geladen</span> }
                    </div>
                </div>
            </div>
        );
    }
}

HighChart.propTypes = {
    style: PropTypes.object,
    plugins: PropTypes.arrayOf(
        PropTypes.oneOfType([ PropTypes.func, PropTypes.object ])
    ),
    config: PropTypes.object,
    item: PropTypes.oneOfType([ PropTypes.object, PropTypes.array ]),
    getNewJournalEntryData: PropTypes.func,
    editJournalTitle: PropTypes.bool,
    printChart: PropTypes.func,
};

export default HighChart;



// WEBPACK FOOTER //
// ./js/charts/HighChartsChart.jsx